import { getClientCode } from 'reducers/Login';
import {
  getItemsFromSO,
  removeItemsFromSO,
  saveItemsToSO,
  getDB,
} from 'services/DB';
import { getTableMeta, SO, Level, EssentialData } from 'services/DB/types';
import { RootDispatch, RootGetState, RootState, ThunkAction } from 'reducers';
import { setActionStatus, setLastSyncTimestamp } from 'actions/essentialData';

import { CAFA_LEVELS } from '../../constants/CAFA';

interface ActionTypes {
  START: string;
  SUCCESS: string;
  ERROR: string;
}

interface ThunkApi {
  dispatch: RootDispatch;
  getState: RootGetState;
  extraParam: unknown;
}

interface FulfilledAction<T> {
  type: ActionTypes['SUCCESS'];
  payload: T;
}

interface RejectedAction<T extends Error = Error> {
  type: ActionTypes['ERROR'];
  payload: T;
  error: T;
}

interface PersisterOptions<A> {
  provideLevel?: (state: RootState, arg: A) => Level;
}

interface CallerOptions {
  forceRefetch?: boolean;
}

export function withLoadingStateActions<A extends any[] = any[], R = void>(
  actionTypes: ActionTypes,
  thunk: (...args: A) => ThunkAction<R>,
) {
  return (
    ...args: A
  ): ThunkAction<FulfilledAction<R> | RejectedAction> => async dispatch => {
    function fulfill(data: R) {
      const finalAction: FulfilledAction<R> = {
        type: actionTypes.SUCCESS,
        payload: data,
      };
      dispatch(finalAction);
      return finalAction;
    }
    function reject(error: any) {
      const finalAction: RejectedAction = {
        type: actionTypes.ERROR,
        payload: error as any,
        error: error as any,
      };
      dispatch(finalAction);
      return finalAction;
    }

    if (actionTypes) dispatch({ type: actionTypes.START });
    try {
      const apiData = await dispatch(thunk(...args));
      return fulfill(apiData);
    } catch (error) {
      return reject(error);
    }
  };
}

export function withIndexedDbPersister<A = any, R = void>(
  idbKey: string,
  thunk: (args: A, thunkApi: ThunkApi) => Promise<R>,
  persisterOptions?: PersisterOptions<A>,
) {
  return (args: A, callerOptions?: CallerOptions): ThunkAction<R> => async (
    dispatch,
    getState,
    extraParam,
  ) => {
    const clientCode = getClientCode(getState());

    if (!callerOptions?.forceRefetch) {
      try {
        const [dbData] = await getItemsFromSO(
          clientCode,
          SO.ESSENTIAL_DATA.NAME,
          [idbKey],
        );
        // Might be null but in such case assume that it's intentional
        if (dbData?.data !== undefined) {
          dispatch(setActionStatus(idbKey, 'DONE'));
          return dbData.data as R;
        }
      } catch (error) {
        // Fallthrough - if failed to fetch from DB the fetch from API
      }
    }

    let apiData: R;
    try {
      apiData = await thunk(args, {
        dispatch,
        getState,
        extraParam,
      });
    } catch (error) {
      dispatch(setActionStatus(idbKey, 'ERROR', error.message));
      throw error;
    }

    try {
      const level = persisterOptions?.provideLevel?.(getState(), args) ?? {
        level: CAFA_LEVELS.Company,
        id: '',
      };
      await saveItemsToSO(
        clientCode,
        SO.ESSENTIAL_DATA.NAME,
        [{ key: idbKey, data: apiData, level }],
        null as never,
      );
      dispatch(setActionStatus(idbKey, 'DONE'));
      return apiData;
    } catch (error) {
      console.warn(
        `Failed to save data fetched by ${idbKey} action to indexed db`,
        error,
      );
      dispatch(setActionStatus(idbKey, 'ERROR', error.message));
      throw error;
    }
  };
}

export function createAsyncThunkWithIndexedDbPersister<A = void, R = void>(
  idbKey: string,
  actionTypes: ActionTypes,
  thunk: (args: A, thunkApi: ThunkApi) => Promise<R>,
  persisterOptions?: PersisterOptions<A>,
) {
  return withLoadingStateActions(
    actionTypes,
    withIndexedDbPersister(idbKey, thunk, persisterOptions),
  );
}

export async function getLastSyncTimestamp(
  clientCode: string,
): Promise<number | undefined> {
  const db = await getDB(clientCode);
  const TABLE_META = getTableMeta(SO.ESSENTIAL_DATA.NAME, 0);
  return db.get(SO.META.NAME, TABLE_META.LAST_SYNC_SERVER);
}

export async function deleteLastSyncTimestamp(clientCode: string) {
  const db = await getDB(clientCode);
  const TABLE_META = getTableMeta(SO.ESSENTIAL_DATA.NAME, 0);
  await db.delete(SO.META.NAME, TABLE_META.LAST_SYNC_SERVER);
}

export async function updateLastSyncTimestamp(
  clientCode: string,
  timestamp: number,
) {
  const db = await getDB(clientCode);
  const TABLE_META = getTableMeta(SO.ESSENTIAL_DATA.NAME, 0);
  await db.put(SO.META.NAME, timestamp, TABLE_META.LAST_SYNC_SERVER);
}

export function invalidateAll() {
  return async (dispatch, getState) => {
    const clientCode = getClientCode(getState());
    const db = await getDB(clientCode);
    const tx = db.transaction(SO.ESSENTIAL_DATA.NAME, 'readwrite');
    await tx.store.clear();
    await deleteLastSyncTimestamp(clientCode);
    dispatch(setLastSyncTimestamp(0));
  };
}

export async function invalidateEssentialData(
  shouldInvalidate: (item: EssentialData['value']) => boolean,
) {
  const clientCode = getClientCode(null);
  const items = await getItemsFromSO(clientCode, SO.ESSENTIAL_DATA.NAME);
  const keysToRemove = items
    .filter(item => shouldInvalidate(item))
    .map(item => item.key);
  await removeItemsFromSO(clientCode, SO.ESSENTIAL_DATA.NAME, keysToRemove);
}
